"use client";

import React from 'react';
import { CloudIcon, CircleStackIcon, CpuChipIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';

const TechnologyStackSection = () => {
  const selectionFactors = [
    {
      icon: CircleStackIcon,
      title: "Data Volume & Complexity",
      description: "Scale requirements drive platform selection–from terabytes to petabytes"
    },
    {
      icon: CloudIcon,
      title: "Cloud Strategy",
      description: "Multi-cloud, hybrid, or on-premise based on your infrastructure roadmap"
    },
    {
      icon: CpuChipIcon,
      title: "Budget & TCO",
      description: "Total cost of ownership analysis including licensing, compute, and storage"
    },
    {
      icon: ShieldCheckIcon,
      title: "Compliance Requirements",
      description: "Regulatory needs (HIPAA, SOC 2, GDPR) influence architecture decisions"
    }
  ];

  const technologyOptions = [
    {
      name: "Snowflake",
      useCase: "Best for: Multi-cloud flexibility, instant scalability, zero maintenance"
    },
    {
      name: "Google BigQuery",
      useCase: "Best for: Serverless analytics, ML integration, real-time streaming"
    },
    {
      name: "Amazon Redshift",
      useCase: "Best for: AWS ecosystem integration, cost optimization, mature tooling"
    },
    {
      name: "Azure Synapse",
      useCase: "Best for: Microsoft stack integration, hybrid scenarios, Power BI native"
    },
    {
      name: "Traditional Enterprise DBs",
      useCase: "Best for: On-premise requirements, existing infrastructure, regulatory constraints"
    },
    {
      name: "Hybrid Multi-Cloud",
      useCase: "Best for: Complex enterprise needs, vendor flexibility, distributed workloads"
    }
  ];

  const architectureDrivers = [
    "Performance requirements and query patterns",
    "Scalability needs for future growth",
    "Regulatory and compliance mandates",
    "Long-term sustainability and vendor ecosystem",
    "Integration with existing technology investments"
  ];

  return (
    <section className="bg-white py-12 sm:py-16 md:py-24 min-h-0 flex items-center">
      <div className="container mx-auto px-4 sm:px-6 max-w-7xl">
        {/* Header */}
        <div className="text-left md:text-center mb-10 sm:mb-12 md:mb-16">
          <p className="text-gold text-xs sm:text-sm font-semibold uppercase tracking-[1.5px] mb-3 sm:mb-4">
            TECHNOLOGY SELECTION
          </p>
          <h2 className="font-serif text-navy text-[26px] sm:text-[34px] md:text-[44px] lg:text-[48px] leading-[1.2] font-bold mb-4 sm:mb-6">
            Right Technology for Your Needs
          </h2>
          <p className="text-charcoal text-[15px] sm:text-base md:text-xl leading-[1.7] max-w-4xl md:mx-auto">
            We select technology based on your specific requirements–not vendor preferences or one-size-fits-all solutions.
          </p>
        </div>

        {/* Selection Factors */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-10 sm:mb-16">
          {selectionFactors?.map((factor, index) => {
            const IconComponent = factor?.icon;
            return (
              <div key={index} className="bg-cream rounded-xl p-4 sm:p-6 border border-gray-200 hover:border-gold transition-all duration-300">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gold/10 flex items-center justify-center mb-3 sm:mb-4">
                  <IconComponent className="w-5 h-5 sm:w-6 sm:h-6 text-gold" />
                </div>
                <h3 className="font-serif text-navy text-base sm:text-lg font-bold mb-1 sm:mb-2">{factor?.title}</h3>
                <p className="text-charcoal text-[13px] sm:text-sm leading-[1.6]">{factor?.description}</p>
              </div>
            );
          })}
        </div>

        {/* Technology Options */}
        <div className="mb-10 sm:mb-16">
          <h3 className="font-serif text-navy text-xl sm:text-2xl md:text-3xl font-bold text-left md:text-center mb-6 sm:mb-10">
            Platform Options We Evaluate
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {technologyOptions?.map((tech, index) => (
              <div key={index} className="bg-cream rounded-xl p-4 sm:p-6 border-2 border-gray-200 hover:shadow-lg transition-all duration-300">
                <h4 className="font-serif text-navy text-lg sm:text-xl font-bold mb-2 sm:mb-3">{tech?.name}</h4>
                <p className="text-charcoal text-[13px] sm:text-sm leading-[1.6]">{tech?.useCase}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Architecture Drivers */}
        <div className="max-w-5xl mx-auto">
          <div className="bg-gradient-to-br from-navy to-navy/90 rounded-xl sm:rounded-2xl p-5 sm:p-6 md:p-8 lg:p-12 shadow-xl">
            <h3 className="font-serif text-white text-xl sm:text-2xl md:text-3xl font-bold text-left md:text-center mb-6 sm:mb-8">
              Architecture Decisions Driven By:
            </h3>
            <div className="space-y-3 sm:space-y-4">
              {architectureDrivers?.map((driver, index) => (
                <div key={index} className="flex items-start gap-3 sm:gap-4 bg-white/5 rounded-lg p-3 sm:p-4 border border-white/10">
                  <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-gold flex items-center justify-center flex-shrink-0">
                    <span className="text-navy text-xs sm:text-sm font-bold">{index + 1}</span>
                  </div>
                  <p className="text-white text-[14px] sm:text-base leading-[1.6] pt-0.5 sm:pt-1">{driver}</p>
                </div>
              ))}
            </div>
            <div className="mt-6 sm:mt-8 pt-6 sm:pt-8 border-t border-white/10 text-left md:text-center">
              <p className="text-white/90 text-[15px] sm:text-base md:text-lg leading-[1.7]">
                <span className="text-gold font-semibold">Not vendor preference.</span> Every recommendation is backed by 
                technical analysis, cost modeling, and alignment with your long-term data strategy.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TechnologyStackSection;